import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getPositionSamples } from '../api/positionSamples'

const WINDOW_PAD_MS = 5 * 60_000 // 5 min before first fill / after last fill for context

const UP_LINE = '#10b981'
const UP_FILL = 'rgba(16,185,129,0.28)'
const DOWN_LINE = '#ef4444'
const DOWN_FILL = 'rgba(239,68,68,0.28)'
const GRAY_LINE = '#52525b'
const GRAY_FILL = 'rgba(82,82,91,0.10)'

export interface ChartFill {
  ts: number
  price: number
  side: 'BUY' | 'SELL'
  quantity: number
}

interface PositionPriceChartProps {
  symbol: string
  fills: ChartFill[]
  /** Whether the position is profitable (drives held-segment color). */
  isUp: boolean
  isOption?: boolean
  height?: number
  /**
   * When true, the position is still open: the right edge of the x-axis tracks
   * the current time (refetched on a 5s interval), no exit divider is drawn,
   * and the live price anchors the right end of the held line so it extends
   * cleanly to "now" even when only the entry fill exists.
   */
  isOpen?: boolean
  /**
   * Live last-trade price for an open position. Used to anchor the right edge
   * of the line when isOpen is true. Ignored when isOpen is false.
   */
  livePrice?: number
}

interface Pt { t: number; p: number }

interface PlacedFill extends ChartFill {
  x: number
  y: number
  leftPct: number
  topPct: number
  variant: 'entry' | 'exit'
  /**
   * Per-fill outcome vs. the running avg cost of the position at the moment
   * of the fill. Null for entry/scale-in fills (no realized P&L). For exits:
   * true if profitable on that fill, false if a loss, null if exactly flat.
   */
  exitIsProfit: boolean | null
  /** Stack index within a cluster of fills that are close together horizontally. */
  stackIdx: number
}

interface TickMark { leftPct: number; label: string }

interface PathBuild {
  preD: string; preFillD: string
  heldD: string; heldFillD: string
  postD: string; postFillD: string
  placedFills: PlacedFill[]
  entryX: number; exitX: number
  hasPre: boolean; hasHeld: boolean; hasPost: boolean
  tickMarks: TickMark[]
}

const SVG_W = 600
const PAD_X = 14
const PAD_Y = 12

function fmtTimeHHMM(ms: number): string {
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  const h24 = d.getHours()
  const m = d.getMinutes()
  const period = h24 >= 12 ? 'pm' : 'am'
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${m.toString().padStart(2, '0')}${period}`
}

const TICK_COUNT = 7

function fmtPrice(p: number): string {
  if (!Number.isFinite(p)) return '—'
  return `$${p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function PositionPriceChart({
  symbol,
  fills,
  isUp,
  isOption = false,
  height = 120,
  isOpen = false,
  livePrice,
}: PositionPriceChartProps) {
  // Collapse fills that share the same second + same side + same price into
  // a single point with summed quantity. The transactions list keeps the raw
  // rows (so the LIMIT vs MARKET audit trail is preserved), but on the chart
  // they're visually identical — rendering them as separate dots/pills just
  // creates overlapping noise.
  const sortedFills = useMemo(() => {
    const groups = new Map<string, ChartFill[]>()
    for (const f of fills) {
      const sec = Math.floor(f.ts / 1000)
      const key = `${sec}|${f.side}|${f.price}`
      const arr = groups.get(key) ?? []
      arr.push(f)
      groups.set(key, arr)
    }
    const merged: ChartFill[] = []
    for (const arr of groups.values()) {
      if (arr.length === 1) {
        merged.push(arr[0])
      } else {
        const totalQty = arr.reduce((s, x) => s + x.quantity, 0)
        merged.push({ ...arr[0], quantity: totalQty })
      }
    }
    return merged.sort((a, b) => a.ts - b.ts)
  }, [fills])

  const entryMs = sortedFills[0]?.ts ?? NaN
  const lastFillMs = sortedFills[sortedFills.length - 1]?.ts ?? NaN
  const openingSide = sortedFills[0]?.side ?? 'BUY'

  const startMs = entryMs - WINDOW_PAD_MS

  const { data, isLoading } = useQuery({
    queryKey: ['position-samples', symbol, entryMs, isOpen ? 'live' : lastFillMs],
    queryFn: () => {
      const end = isOpen ? Date.now() : lastFillMs
      return getPositionSamples(symbol, startMs, end + WINDOW_PAD_MS)
    },
    enabled:
      Number.isFinite(entryMs) &&
      (isOpen ? true : Number.isFinite(lastFillMs) && entryMs <= lastFillMs),
    staleTime: isOpen ? 0 : 60_000,
    refetchInterval: isOpen ? 5000 : false,
    refetchIntervalInBackground: false,
  })

  // In live mode, the right edge of the chart tracks "now" — anchored to the
  // server's reported endMs when available, falling back to wall-clock time
  // until the first sample lands.
  const exitMs = isOpen ? (data?.endMs ?? Math.max(lastFillMs, Date.now())) : lastFillMs

  const built = useMemo<PathBuild | null>(() => {
    if (!sortedFills.length) return null
    if (!Number.isFinite(entryMs) || !Number.isFinite(exitMs)) return null

    const points: Pt[] = data?.points ?? []

    // Bucket samples into pre / held / post. In live/open mode there's no exit
    // event, so every sample after entry is part of the live held segment.
    const pre: Pt[] = []
    const held: Pt[] = []
    const post: Pt[] = []
    for (const pt of points) {
      if (pt.t < entryMs) pre.push(pt)
      else if (!isOpen && pt.t > lastFillMs) post.push(pt)
      else held.push(pt)
    }

    // Inject ALL fill prices into the held segment so the line passes exactly
    // through each fill dot. Samples within a few seconds of a fill are
    // removed so the captured "last" price (which may diverge from the actual
    // fill price, especially for options with wide bid/ask) doesn't tug the
    // line off the dot.
    const FILL_NEIGHBOR_MS = 3_000
    const fillTimes = sortedFills.map((f) => f.ts)
    const farFromFills = (pt: Pt) =>
      fillTimes.every((ft) => Math.abs(pt.t - ft) > FILL_NEIGHBOR_MS)

    let heldStitched: Pt[] = held.filter(farFromFills)
    for (const f of sortedFills) heldStitched.push({ t: f.ts, p: f.price })
    // In live mode, anchor the right edge of the line at the current live
    // price so the chart tracks "now" — without this a freshly-opened position
    // would render as a single dot until samples accumulate, and the line
    // would trail the live quote between server flushes.
    if (isOpen && livePrice != null && Number.isFinite(livePrice) && livePrice > 0) {
      heldStitched.push({ t: exitMs, p: livePrice })
    }
    heldStitched.sort((a, b) => a.t - b.t)

    // Stitch pre→held and held→post boundaries so each gray segment ends /
    // starts at the entry / exit fill point — the line stays visually
    // continuous across segments.
    const preStitched = pre.filter(farFromFills)
    if (preStitched.length) {
      preStitched.push({ t: entryMs, p: sortedFills[0].price })
    }
    const postStitched = post.filter(farFromFills)
    if (postStitched.length) {
      postStitched.unshift({
        t: exitMs,
        p: sortedFills[sortedFills.length - 1].price,
      })
    }

    // If we have no samples at all, the fills alone are still enough to draw a
    // line through the dots.
    if (heldStitched.length < 2) {
      if (sortedFills.length < 2) return null
      heldStitched = sortedFills.map((f) => ({ t: f.ts, p: f.price }))
    }

    // Reassign the working segments to the stitched copies.
    pre.length = 0
    pre.push(...preStitched)
    held.length = 0
    held.push(...heldStitched)
    post.length = 0
    post.push(...postStitched)

    // y-range: include every fill price + every sample.
    const allY = [...held, ...pre, ...post].map((s) => s.p)
    for (const f of sortedFills) allY.push(f.price)
    const yMin = Math.min(...allY)
    const yMax = Math.max(...allY)
    const yPad = (yMax - yMin) * 0.18 || Math.max(1, yMin * 0.01)
    const lo = yMin - yPad
    const hi = yMax + yPad

    // x-range
    const tMin = (pre[0]?.t ?? held[0].t) - 1000
    const tMax = (post[post.length - 1]?.t ?? held[held.length - 1].t) + 1000
    const xStart = PAD_X
    const xEnd = SVG_W - PAD_X
    const yTop = PAD_Y
    const yBot = height - PAD_Y

    const mapX = (t: number) =>
      tMax <= tMin ? xStart : xStart + ((t - tMin) / (tMax - tMin)) * (xEnd - xStart)
    const mapY = (v: number) => yTop + (1 - (v - lo) / (hi - lo)) * (yBot - yTop)

    /**
     * Catmull-Rom spline → cubic Bezier path. Unlike quadratic-midpoint
     * smoothing, this curve passes exactly through every input point — so
     * dots placed at fill positions sit on the line, not above/below it.
     */
    function pathD(seg: Pt[]): string {
      if (!seg.length) return ''
      const mapped = seg.map((s) => ({ x: mapX(s.t), y: mapY(s.p) }))
      if (mapped.length === 1) {
        return `M${mapped[0].x.toFixed(2)} ${mapped[0].y.toFixed(2)}`
      }
      let d = `M${mapped[0].x.toFixed(2)} ${mapped[0].y.toFixed(2)}`
      for (let i = 0; i < mapped.length - 1; i++) {
        const p0 = i > 0 ? mapped[i - 1] : mapped[i]
        const p1 = mapped[i]
        const p2 = mapped[i + 1]
        const p3 = i + 2 < mapped.length ? mapped[i + 2] : mapped[i + 1]
        const c1x = p1.x + (p2.x - p0.x) / 6
        const c1y = p1.y + (p2.y - p0.y) / 6
        const c2x = p2.x - (p3.x - p1.x) / 6
        const c2y = p2.y - (p3.y - p1.y) / 6
        d += ` C${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`
      }
      return d
    }

    function fillD(d: string, seg: Pt[]): string {
      if (!d || seg.length < 2) return ''
      const firstX = mapX(seg[0].t).toFixed(2)
      const lastX = mapX(seg[seg.length - 1].t).toFixed(2)
      return `${d} L${lastX} ${yBot.toFixed(2)} L${firstX} ${yBot.toFixed(2)} Z`
    }

    // Walk fills chronologically: track running totalQty + totalCost to
    // compute per-fill outcome (profit / loss / breakeven vs. the avg entry
    // price at the moment of the fill). Also compute cluster-aware stack idx
    // so pills don't pile up when fills land within a few SVG pixels.
    const CLUSTER_X_THRESHOLD = 60
    const placedFills: PlacedFill[] = []
    let lastClusterX = -Infinity
    let stackIdx = 0
    let totalQty = 0
    let totalCost = 0
    for (const f of sortedFills) {
      const isOpening = f.side === openingSide
      let exitIsProfit: boolean | null = null
      if (isOpening) {
        // Opening or scaling in: add to position, refresh avg cost.
        totalCost += f.quantity * f.price
        totalQty += f.quantity
      } else {
        // Closing fill: judge against the avg cost up to this point.
        const avgCost = totalQty > 0 ? totalCost / totalQty : f.price
        if (f.price > avgCost) exitIsProfit = openingSide === 'BUY' ? true : false
        else if (f.price < avgCost) exitIsProfit = openingSide === 'BUY' ? false : true
        else exitIsProfit = null
        // Reduce the position; keep avg cost stable on partial closes.
        totalCost -= f.quantity * avgCost
        totalQty -= f.quantity
        if (totalQty <= 0) {
          totalQty = 0
          totalCost = 0
        }
      }

      const x = mapX(f.ts)
      const y = mapY(f.price)
      if (x - lastClusterX < CLUSTER_X_THRESHOLD) stackIdx++
      else stackIdx = 0
      lastClusterX = x

      placedFills.push({
        ...f,
        x,
        y,
        leftPct: (x / SVG_W) * 100,
        topPct: (y / height) * 100,
        variant: isOpening ? 'entry' : 'exit',
        exitIsProfit,
        stackIdx,
      })
    }

    const preD = pathD(pre)
    const heldD = pathD(held)
    const postD = pathD(post)

    const tickMarks: TickMark[] = []
    for (let i = 0; i < TICK_COUNT; i++) {
      const frac = i / (TICK_COUNT - 1)
      const t = tMin + frac * (tMax - tMin)
      const x = mapX(t)
      tickMarks.push({
        leftPct: (x / SVG_W) * 100,
        label: fmtTimeHHMM(t),
      })
    }

    return {
      preD, preFillD: fillD(preD, pre),
      heldD, heldFillD: fillD(heldD, held),
      postD, postFillD: fillD(postD, post),
      placedFills,
      entryX: mapX(entryMs),
      exitX: mapX(exitMs),
      hasPre: pre.length >= 2,
      hasHeld: held.length >= 2,
      hasPost: post.length >= 2,
      tickMarks,
    }
  }, [data, sortedFills, entryMs, exitMs, lastFillMs, openingSide, height, isOpen, livePrice])

  const lineColor = isUp ? UP_LINE : DOWN_LINE
  const fillColor = isUp ? UP_FILL : DOWN_FILL
  const dividerColor = isUp ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'

  if (isLoading && !built) {
    return (
      <div
        className="rounded bg-zinc-900/40 animate-pulse"
        style={{ height }}
        aria-label="Loading chart"
      />
    )
  }

  if (!built) {
    return (
      <div
        className="rounded border border-dashed border-zinc-800 text-[10px] text-zinc-500 flex items-center justify-center px-2 text-center"
        style={{ height }}
      >
        No price samples captured for this position
      </div>
    )
  }

  return (
    <div className="relative" style={{ height }}>
      <svg
        viewBox={`0 0 ${SVG_W} ${height}`}
        preserveAspectRatio="none"
        className="block w-full h-full"
      >
        {/* dashed dividers at the first and last fill — the second is the
            actual exit for a closed position; suppressed while live since
            there's no exit event yet. */}
        <line
          x1={built.entryX} x2={built.entryX} y1={PAD_Y} y2={height - PAD_Y}
          stroke={dividerColor} strokeWidth={1} strokeDasharray="2 2"
        />
        {!isOpen && (
          <line
            x1={built.exitX} x2={built.exitX} y1={PAD_Y} y2={height - PAD_Y}
            stroke={dividerColor} strokeWidth={1} strokeDasharray="2 2"
          />
        )}

        {built.hasPre && (
          <>
            <path d={built.preFillD} fill={GRAY_FILL} />
            <path
              d={built.preD} fill="none" stroke={GRAY_LINE} strokeWidth={1.5}
              strokeLinejoin="round" strokeLinecap="round"
            />
          </>
        )}

        {built.hasHeld && (
          <>
            <path d={built.heldFillD} fill={fillColor} />
            <path
              d={built.heldD} fill="none" stroke={lineColor} strokeWidth={2}
              strokeLinejoin="round" strokeLinecap="round"
            />
          </>
        )}

        {built.hasPost && (
          <>
            <path d={built.postFillD} fill={GRAY_FILL} />
            <path
              d={built.postD} fill="none" stroke={GRAY_LINE} strokeWidth={1.5}
              strokeLinejoin="round" strokeLinecap="round"
            />
          </>
        )}

        {/* anchor dots — one per fill */}
        {built.placedFills.map((f, i) => (
          <circle
            key={`${f.ts}-${i}`}
            cx={f.x}
            cy={f.y}
            r={3.5}
            fill={lineColor}
            stroke="#0c0c0f"
            strokeWidth={2}
          />
        ))}
      </svg>

      {built.placedFills.map((f, i) => (
        <FillPill
          key={`${f.ts}-${i}-pill`}
          leftPct={f.leftPct}
          topPct={f.topPct}
          fill={f}
          isOption={isOption}
        />
      ))}

      <div className="absolute inset-x-0 -bottom-4 h-3 font-mono text-[9px] text-zinc-500">
        {built.tickMarks.map((tick, i) => {
          const transform =
            i === 0
              ? 'translateX(0)'
              : i === built.tickMarks.length - 1
                ? 'translateX(-100%)'
                : 'translateX(-50%)'
          return (
            <span
              key={i}
              className="absolute top-0 whitespace-nowrap"
              style={{ left: `${tick.leftPct}%`, transform }}
            >
              {tick.label}
            </span>
          )
        })}
      </div>
    </div>
  )
}

interface FillPillProps {
  leftPct: number
  topPct: number
  fill: PlacedFill
  isOption: boolean
}

const PILL_HEIGHT_PX = 20

function FillPill({ leftPct, topPct, fill, isOption }: FillPillProps) {
  // Color per fill, not per cycle: a partial close at a loss inside an overall
  // winning cycle still reads red on its own pill.
  let borderClass: string
  let sideClass: string
  if (fill.variant === 'entry') {
    borderClass = 'border-zinc-700'
    sideClass = 'text-zinc-500'
  } else if (fill.exitIsProfit === true) {
    borderClass = 'border-emerald-500/60'
    sideClass = 'text-emerald-400'
  } else if (fill.exitIsProfit === false) {
    borderClass = 'border-red-500/60'
    sideClass = 'text-red-400'
  } else {
    // Exactly breakeven exit — neutral styling.
    borderClass = 'border-zinc-600'
    sideClass = 'text-zinc-400'
  }

  // Stagger algorithm for clustered fills:
  //   stackIdx 0 → directly above the dot
  //   stackIdx 1 → directly below
  //   stackIdx 2 → above + one pill height
  //   stackIdx 3 → below + one pill height
  // ... so each pair (above/below) gets pushed further from the dot.
  const above = fill.stackIdx % 2 === 0
  const extraRows = Math.floor(fill.stackIdx / 2)
  const extraPx = extraRows * (PILL_HEIGHT_PX + 2)
  const transform = above
    ? `translate(-50%, calc(-100% - ${4 + extraPx}px))`
    : `translate(-50%, ${8 + extraPx}px)`

  const unit = isOption ? 'ct' : 'sh'

  return (
    <div
      className={`absolute -translate-x-1/2 bg-zinc-950 border ${borderClass} rounded px-1.5 py-0.5 font-mono text-[10px] leading-tight whitespace-nowrap pointer-events-none`}
      style={{
        left: `${Math.max(2, Math.min(98, leftPct))}%`,
        top: `${Math.max(0, topPct)}%`,
        transform,
      }}
    >
      <span className={`mr-1 text-[9px] uppercase tracking-wide ${sideClass}`}>
        {fill.side}
      </span>
      <span className="text-zinc-300">
        {fill.quantity}{unit}
      </span>
      <span className="mx-1 text-zinc-700">·</span>
      <span className="text-zinc-100">{fmtPrice(fill.price)}</span>
    </div>
  )
}
